"use client";

import type { ReactNode } from "react";
import React, { useEffect, useState } from "react";

import { cn } from "../../utils";
import { Icon } from "../Icon";

export interface SummaryCollapseProps {
  title?: ReactNode;
  isOpen?: boolean;
  children?: ReactNode;
  className?: string;
  contentClassName?: string;
  onIsOpenChange?: (isOpen: boolean) => void;
  variant?: "default" | "minimal";
}

export const SummaryCollapse: React.FC<SummaryCollapseProps> = ({
  title,
  isOpen = false,
  children,
  className,
  contentClassName,
  onIsOpenChange,
  variant = "default",
}) => {
  const [summaryCollapseIsOpen, setSummaryCollapseIsOpen] = useState(isOpen);

  const isDefault = variant === "default";
  const isMinimal = variant === "minimal";

  useEffect(() => {
    setSummaryCollapseIsOpen(isOpen);
  }, [isOpen]);

  const handleToggle = () => {
    setSummaryCollapseIsOpen((previous) => {
      onIsOpenChange?.(!previous);

      return !previous;
    });
  };

  return (
    <div
      className={cn(
        "ui:grid ui:w-full ui:grid-cols-[minmax(0,1fr)] ui:overflow-hidden ui:rounded-sm ui:transition-[grid-template-rows,background-color] ui:duration-200",
        summaryCollapseIsOpen
          ? "ui:grid-rows-[max-content_1fr]"
          : "ui:grid-rows-[max-content_0fr]",
        {
          "ui:bg-buttonBg ui:hover:bg-buttonHover ui:dark:bg-buttonFill":
            isDefault,
        },
        className,
      )}
    >
      <button
        type="button"
        className={cn(
          "ui:flex ui:h-11 ui:w-full ui:cursor-pointer ui:items-center ui:justify-between ui:border-b ui:border-transparent ui:bg-transparent ui:px-4 ui:py-0 ui:text-left ui:text-textPrimary ui:transition-colors",
          {
            "ui:border-divider": summaryCollapseIsOpen && isDefault,
            "ui:hover:text-textPrimary/80": isMinimal,
          },
        )}
        onClick={handleToggle}
      >
        <span
          className={cn({
            "ui:text-base": isMinimal,
          })}
        >
          {title}
        </span>
        <Icon
          size="xs"
          name="caret-down"
          className={cn("ui:transition-transform", {
            "ui:rotate-180": summaryCollapseIsOpen,
          })}
        />
      </button>

      <div className="ui:min-h-0 ui:overflow-hidden">
        <div
          className={cn(
            {
              "ui:px-4 ui:py-3": isDefault,
            },
            contentClassName,
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
};
